import {
  ChangeDetectorRef,
  Component,
  DoCheck,
  effect,
  inject,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlContainer,
  FormBuilder,
  FormControl,
  FormGroupDirective,
  NgControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FormInputComponent } from './form-input.component';
import { LanguageService } from '../../../services/language/language.service';
import {
  EEntityType,
  ETextType,
  INormalText,
  LanguagePrefix,
  ITextLanguageValue,
  TextRelationType,
} from '@web/dtos';
import { Subscription } from 'rxjs';

@Component({
  selector: 'aw-form-input',
  imports: [CommonModule],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  template: '',
})
export class FormTranslationInputComponent
  extends FormInputComponent
  implements DoCheck, OnDestroy, OnInit
{
  protected readonly formBuilder: FormBuilder = inject(FormBuilder);
  protected readonly cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  protected readonly languageService: LanguageService = inject(LanguageService);

  public entityType: InputSignal<EEntityType> = input.required<EEntityType>();
  public relationType: InputSignal<TextRelationType> =
    input.required<TextRelationType>();

  protected readonly activeTextValue: FormControl<string> =
    this.formBuilder.nonNullable.control('');
  private boundControl: AbstractControl<INormalText[]> | null = null;
  private controlSubscription: Subscription = new Subscription();
  private readonly translationValidator: ValidatorFn =
    this.hasAtLeastOneTextAndValue();

  constructor(public override ngControl: NgControl) {
    super(ngControl);

    effect(() => {
      this.languageService.activeLanguage();
      this.setActiveTextForLanguage();
    });

    this.subscription.add(
      this.activeTextValue.valueChanges.subscribe(value => {
        let normalText: INormalText = (
          this.ngControl.control?.value as INormalText[]
        )[0];
        const activeLangPrefix: LanguagePrefix =
          this.languageService.activeLanguage().prefix;

        if (normalText) {
          let hasChanged = false;
          const newTextValue: ITextLanguageValue[] = normalText.text.values.map(
            (textValue: ITextLanguageValue) => {
              const newTextValue: ITextLanguageValue = { ...textValue };
              if (textValue.languagePrefix === activeLangPrefix) {
                hasChanged = true;
                newTextValue.value = value;
              }
              return newTextValue;
            }
          );

          if (!hasChanged) {
            newTextValue.push({
              value: value,
              languagePrefix: activeLangPrefix,
            });
          }

          normalText.text.values = newTextValue;
        } else {
          normalText = {
            textType: ETextType.Web,
            entityType: this.entityType(),
            relationType: this.relationType(),
            text: {
              values: [
                {
                  value,
                  languagePrefix: this.languageService.activeLanguage().prefix,
                },
              ],
            },
          };
        }

        this.ngControl.control?.setValue([normalText]);
        this.ngControl.control?.updateValueAndValidity();
        this.ngControl.control?.markAsDirty();
        this.ngControl.control?.markAsTouched();
        this.cdRef.detectChanges();
      })
    );
  }

  private setActiveTextForLanguage() {
    const text: string | undefined =
      this.languageService.getActiveLanguageTextValue(
        this.ngControl.control?.value[0]
      );
    this.activeTextValue.setValue(text ?? '', { emitEvent: false });
  }

  ngOnInit(): void {
    this.bindControl();
  }

  ngDoCheck(): void {
    this.bindControl();
  }

  override ngOnDestroy(): void {
    this.controlSubscription.unsubscribe();
    this.boundControl?.removeValidators(this.translationValidator);
    super.ngOnDestroy();
  }

  private bindControl(): void {
    const control = this.ngControl.control as AbstractControl<
      INormalText[]
    > | null;

    if (!control || control === this.boundControl) {
      return;
    }

    this.controlSubscription.unsubscribe();
    this.boundControl?.removeValidators(this.translationValidator);
    this.boundControl = control;
    this.controlSubscription = new Subscription();

    this.controlSubscription.add(
      control.statusChanges.subscribe((): void => {
        if (control.disabled && this.activeTextValue.enabled) {
          this.activeTextValue.disable({ emitEvent: false });
        } else if (control.enabled && this.activeTextValue.disabled) {
          this.activeTextValue.enable({ emitEvent: false });
        }
      })
    );
    this.controlSubscription.add(
      control.valueChanges.subscribe((): void => {
        this.setActiveTextForLanguage();
      })
    );

    if (control.hasValidator(Validators.required)) {
      control.addValidators(this.translationValidator);
      control.updateValueAndValidity({ emitEvent: false });
    }

    if (control.disabled) {
      this.activeTextValue.disable({ emitEvent: false });
    } else {
      this.activeTextValue.enable({ emitEvent: false });
    }
    this.setActiveTextForLanguage();
  }

  public hasAtLeastOneTextAndValue(
    isoCode: string | undefined = undefined
  ): ValidatorFn {
    return (
      control: AbstractControl<INormalText[]>
    ): ValidationErrors | null => {
      if (
        control.value === undefined ||
        control.value === null ||
        control.value.length === 0
      ) {
        return { atLeastOneText: false };
      }
      const hasText = control.value[0].text.values.find(
        (textValue: ITextLanguageValue) => {
          return (
            textValue.value &&
            textValue.value.length > 0 &&
            (isoCode ? textValue.languagePrefix === isoCode : true)
          );
        }
      );
      return !hasText ? { atLeastOneText: true } : null;
    };
  }
}
